"use client"

import { useState } from "react"
import axios from "axios"
import { API_URL } from "../../config"
import { useLanguage } from "../../context/LanguageContext"
import ParameterSelectionModal from "./ParameterSelectionModal"
import "./WeatherUploader.css"

const WeatherUploader = ({ onUpload, onDataMapping, defaultLocation }) => {
  const { t } = useLanguage()
  const [dragActive, setDragActive] = useState(false)
  const [file, setFile] = useState(null)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [uploading, setUploading] = useState(false)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e) => {
    e.preventDefault()

    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = (file) => {
    // Check file type
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls") && !file.name.endsWith(".csv")) {
      setError("Please upload an Excel or CSV file")
      return
    }

    setFile(file)
    setError(null)
    setUploading(true)

    // Read file
    const reader = new FileReader()
    reader.onload = async (event) => {
      try {
        // In a real app, you would parse the file here
        // For this example, we'll simulate parsing with dummy data
        const formData = new FormData()
        formData.append("file", file)

        // Try to upload the file to the server
        try {
          const response = await axios.post(`${API_URL}/api/weather/upload`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })

          if (response.data) {
            onUpload(response.data)
            // Map the data to meteorological fields
            mapDataToMeteorologicalFields(response.data)
          }
        } catch (err) {
          console.error("Error uploading file:", err)
          // Fallback to simulated data if upload fails
          const parsedData = simulateParsing(file.name)
          onUpload(parsedData)
          mapDataToMeteorologicalFields(parsedData)
        }
      } catch (err) {
        setError("Failed to parse file. Please check the format.")
        console.error(err)
      } finally {
        setUploading(false)
      }
    }
    reader.readAsText(file)
  }

  // Simulate parsing the file (in a real app, you would use a library like xlsx or papaparse)
  const simulateParsing = (filename) => {
    // This is just dummy data for demonstration
    return [
      {
        date: new Date().toISOString(),
        tempMax: 25,
        tempMin: 15,
        tempAvg: 20,
        stationFetch: 100,
        humidityMax: 85,
        humidityMin: 45,
        humidityAvg: 65,
        uz: 3.2,
        ud: 2.5,
        un: 1.8,
        zw: 10,
        zp: 2,
        n: 8.5,
        rs: 18.5,
        rn: 12.3,
        epan: 5.2,
        windSpeed: 3.2,
        precipitation: 0,
      },
      {
        date: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        tempMax: 24,
        tempMin: 14,
        tempAvg: 19,
        stationFetch: 100,
        humidityMax: 90,
        humidityMin: 50,
        humidityAvg: 70,
        uz: 2.8,
        ud: 2.2,
        un: 1.5,
        zw: 10,
        zp: 2,
        n: 7.8,
        rs: 17.2,
        rn: 11.5,
        epan: 4.8,
        windSpeed: 2.8,
        precipitation: 5,
      },
    ]
  }

  // Map the parsed data to meteorological fields
  const mapDataToMeteorologicalFields = (data) => {
    if (!data || data.length === 0) return

    // Use the first row of data for mapping
    const firstRow = data[0]

    // Create meteorological data object based on the uploaded data
    const meteorologicalData = {
      temperature: {
        tmax: firstRow.tempMax !== undefined ? String(firstRow.tempMax) : "",
        ta: firstRow.tempAvg !== undefined ? String(firstRow.tempAvg) : "",
        tmin: firstRow.tempMin !== undefined ? String(firstRow.tempMin) : "",
        station: "",
        fetch: firstRow.stationFetch !== undefined ? String(firstRow.stationFetch) : "",
      },
      humidity: {
        rhmax: firstRow.humidityMax !== undefined ? String(firstRow.humidityMax) : "",
        rha: firstRow.humidityAvg !== undefined ? String(firstRow.humidityAvg) : "",
        rhmin: firstRow.humidityMin !== undefined ? String(firstRow.humidityMin) : "",
        uz: firstRow.uz !== undefined ? String(firstRow.uz) : "",
        ud: firstRow.ud !== undefined ? String(firstRow.ud) : "",
        un: firstRow.un !== undefined ? String(firstRow.un) : "",
        zw: firstRow.zw !== undefined ? String(firstRow.zw) : "",
        zp: firstRow.zp !== undefined ? String(firstRow.zp) : "",
      },
      radiation: {
        n: firstRow.n !== undefined ? String(firstRow.n) : "",
        rs: firstRow.rs !== undefined ? String(firstRow.rs) : "",
        rn: firstRow.rn !== undefined ? String(firstRow.rn) : "",
        epan: firstRow.epan !== undefined ? String(firstRow.epan) : "",
      },
    }

    // Pass the mapped data to the parent component
    if (onDataMapping) {
      onDataMapping(meteorologicalData)
    }
  }

  const openTemplateModal = () => {
    setShowModal(true)
  }

  const handleParameterSelection = (selectedParameters) => {
    // Generate and download Excel template based on selected parameters
    generateExcelTemplate(selectedParameters)
    setShowModal(false)
  }

  const generateExcelTemplate = async (parameters) => {
    try {
      // Create header row based on selected parameters
      const headers = ["Date"]

      // Add location columns if coordinates are available
      if (defaultLocation && defaultLocation.coordinates) {
        headers.push("Latitude", "Longitude")
      }

      // Temperature fields
      if (parameters.temperature.selected) {
        if (parameters.temperature.fields.tmax) headers.push("Temperature Max (°C)")
        if (parameters.temperature.fields.ta) headers.push("Temperature Avg (°C)")
        if (parameters.temperature.fields.tmin) headers.push("Temperature Min (°C)")
        if (parameters.temperature.fields.stationFetch) headers.push("Station fetch (m)")
      }

      // Humidity fields
      if (parameters.humidity.selected) {
        if (parameters.humidity.fields.rhmax) headers.push("Humidity Max (%)")
        if (parameters.humidity.fields.rha) headers.push("Humidity Avg (%)")
        if (parameters.humidity.fields.rhmin) headers.push("Humidity Min (%)")
        if (parameters.humidity.fields.uz) headers.push("Uz (m/s)")
        if (parameters.humidity.fields.ud) headers.push("Ud (m/s)")
        if (parameters.humidity.fields.un) headers.push("Un (m/s)")
        if (parameters.humidity.fields.zw) headers.push("Zw (m)")
      }

      // Wind Speed fields
      if (parameters.windSpeed.selected) {
        if (parameters.windSpeed.fields.zp) headers.push("Zp")
        headers.push("Wind Speed (m/s)")
      }

      // Radiation fields
      if (parameters.radiation.selected) {
        if (parameters.radiation.fields.n) headers.push("n (hour)")
        if (parameters.radiation.fields.rs) headers.push("Rs (MJ/m².day)")
        if (parameters.radiation.fields.rn) headers.push("Rn (MJ/m².day)")
        if (parameters.radiation.fields.epan) headers.push("Epan (mm/day)")
      }

      // Add precipitation as it's commonly needed
      headers.push("Precipitation (mm)")

      // Create example rows
      const exampleRows = []

      // Add current date
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      // Format dates
      const formatDate = (date) => {
        return date.toISOString().split("T")[0]
      }

      // Create two example rows with dates
      for (const date of [today, tomorrow]) {
        const row = [formatDate(date)]

        // Add coordinates if available
        if (defaultLocation && defaultLocation.coordinates) {
          row.push(defaultLocation.coordinates.lat.toFixed(6), defaultLocation.coordinates.lng.toFixed(6))
        }

        // Fill the rest with empty values
        for (let i = row.length; i < headers.length; i++) {
          row.push("")
        }

        exampleRows.push(row)
      }

      // Try to get template from server
      try {
        const response = await axios.get(`${API_URL}/api/weather/template`, {
          params: {
            parameters: JSON.stringify(parameters),
            country: defaultLocation?.country || "",
            district: defaultLocation?.district || "",
            lat: defaultLocation?.coordinates?.lat || "",
            lng: defaultLocation?.coordinates?.lng || "",
            locationName: defaultLocation?.locationName || "",
          },
          responseType: "blob",
        })

        // Create a download link for the blob
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", "weather_data_template.xlsx")
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      } catch (err) {
        console.error("Error getting template from server:", err)
        // Fall back to CSV generation if server request fails
      }

      // Create CSV content as fallback
      const csvContent = [headers.join(","), ...exampleRows.map((row) => row.join(","))].join("\n")

      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })

      // Create a download link and trigger the download
      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)
      link.setAttribute("href", url)
      link.setAttribute("download", "weather_data_template.csv")
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Error generating template:", err)
      setError("Failed to generate template")
    }
  }

  return (
    <div className="weather-uploader">
      <div className="weather-data-info">
        <p>{t("Weather data should include:")}</p>
        <ul>
          {/* <li>{t("date")}</li>
          <li>{t("temperature")}</li>
          <li>{t("humidity")}</li>
          <li>{t("wind speed")}</li> */}
          {/* <li>{t("solar radiation")}</li> */}
        </ul>
        <p className="format-note">{t("Data should be in the given format")}</p>

        <button className="template-button" onClick={openTemplateModal}>
          <i className="fas fa-download"></i>
          {t("Download Template")}
        </button>
      </div>

      <div
        className={`upload-area ${dragActive ? "drag-active" : ""}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <div className="upload-icon">
          <i className="fas fa-file-upload"></i>
        </div>
        <p>{t("Drag & drop your Excel file or click to browse")}</p>
        <small>{t("Accepts .xlsx, .xls, .csv files up to 10MB")}</small>

        <input type="file" id="file-upload" onChange={handleChange} accept=".xlsx,.xls,.csv" className="file-input" />

        <label htmlFor="file-upload" className="browse-button">
          {t("Browse Files")}
        </label>

        {file && (
          <div className="file-info">
            <i className="fas fa-file-excel"></i>
            <span>{file.name}</span>
          </div>
        )}

        {uploading && (
          <div className="uploading-indicator">
            <i className="fas fa-spinner fa-spin"></i>
            <span>{t("Uploading and processing...")}</span>
          </div>
        )}

        {error && (
          <div className="upload-error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Parameter Selection Modal */}
      {showModal && (
        <ParameterSelectionModal
          onClose={() => setShowModal(false)}
          onConfirm={handleParameterSelection}
          defaultLocation={defaultLocation}
        />
      )}
    </div>
  )
}

export default WeatherUploader
